// utils/Cryptoutils.js
// ============================================================
// END-TO-END ENCRYPTION UTILITIES
// Uses WebCrypto API (browser-native, no extra deps)
//
// ── DM PROTOCOL (ECDH + AES-GCM per message) ──────────────
//   1. On first load, generate ECDH keypair → store privKey in
//      IndexedDB (via fsDB), register pubKey with server.
//   2. To SEND: fetch recipient pubKey → ephemeral ECDH derive
//      shared secret → AES-GCM encrypt → send
//      { ciphertext, iv, ephemeralPublicKey }.
//   3. To RECEIVE: use stored privKey + ephemeralPublicKey
//      → derive shared secret → AES-GCM decrypt → plaintext.
//
// ── GROUP PROTOCOL (Shared Symmetric Key) ─────────────────
//   One AES-GCM-256 key per group, shared among all members.
//   Distribution: the group key is ECDH-wrapped individually
//   for each member (same envelope as DM text) and stored in
//   MongoDB. Members decrypt their wrapped copy to recover the
//   raw AES key, then cache it in localStorage.
//
//   Send:  encryptWithGroupKey(plaintext, groupKey) → { ciphertext, iv }
//   Recv:  decryptWithGroupKey(ciphertext, iv, groupKey) → plaintext
//
//   Key lifecycle:
//     generateGroupKey()              → CryptoKey (AES-GCM-256)
//     exportGroupKey(key)             → base64 string (raw bytes)
//     importGroupKey(base64)          → CryptoKey
//     encryptGroupKeyForMember(rawB64, memberPubKey) → DM-style envelope
//     decryptGroupKey(envelope, myPrivKey)           → base64 raw key
//
//   localStorage: group key stored at "group_key_<groupId>"
// ============================================================
import {
  savePrivateKey,
  loadPrivateKeyFromDB,
  saveGroupCryptoKey,
  loadGroupCryptoKey,

  saveSigningPrivateKey,
  loadSigningPrivateKey as loadSigningPrivateKeyFromDB,
  saveStatusCryptoKey,
  loadStatusCryptoKey,
  saveSolanaPrivateKey,
  loadSolanaPrivateKey,

} from "../db/fsDB";

import { Keypair } from "@solana/web3.js";
import nacl from "tweetnacl";

export { savePrivateKey };

const ALGO     = "ECDH";
const CURVE    = "P-256";
const AES_ALGO = "AES-GCM";
const AES_KEY_LEN = 256;

const PUBKEY_STORAGE_PREFIX   = "e2ee_pubkey_";
const GROUP_KEY_STORAGE_PREFIX = "group_key_";
const SIGNING_PUBKEY_STORAGE_PREFIX = "signing_pubkey_";


/* ─────────────────────────────────────────────────────────────
   ECDH KEYPAIR — DM identity keys
───────────────────────────────────────────────────────────── */
export const generateKeyPair = async () => {
  const keyPair = await crypto.subtle.generateKey(
    { name: ALGO, namedCurve: CURVE },
    true, // ✅ FIXED (must be extractable)
    ["deriveKey", "deriveBits"]
  );

  const publicJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);

  return { privateKey: keyPair.privateKey, publicJwk };
};

export const saveKeyPair = async (chatId, privateKey, publicJwk) => {
  // ✅ store private key securely in IDB
  await savePrivateKey(chatId, privateKey);

  // ✅ public key can remain in localStorage
  localStorage.setItem(`${PUBKEY_STORAGE_PREFIX}${chatId}`, JSON.stringify(publicJwk));
};

export const loadPrivateKey = async (chatId) => {
  try {
    const key = await loadPrivateKeyFromDB(chatId);
    return key || null;
  } catch (e) {
    return null;
  }
};

export const loadPublicKeyJwk = (chatId) => {
  const raw = localStorage.getItem(`${PUBKEY_STORAGE_PREFIX}${chatId}`);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const importPublicKey = async (jwk) =>
  crypto.subtle.importKey(
    "jwk", jwk,
    { name: ALGO, namedCurve: CURVE },
    true,
    []
  );

const deriveSharedKey = async (privateKey, publicKey) =>
  crypto.subtle.deriveKey(
    { name: ALGO, public: publicKey },
    privateKey,
    { name: AES_ALGO, length: AES_KEY_LEN },
    false,
    ["encrypt", "decrypt"]
  );

/* ─────────────────────────────────────────────────────────────
   INTERNAL — encrypt raw bytes with ephemeral ECDH
───────────────────────────────────────────────────────────── */
const _encryptBytes = async (bytes, recipientPublicKeyJwk) => {
  const ephemeral = await crypto.subtle.generateKey(
    { name: ALGO, namedCurve: CURVE },
    true,
    ["deriveKey", "deriveBits"]
  );
  const ephemeralPublicKey = await crypto.subtle.exportKey("jwk", ephemeral.publicKey);
  const recipientPubKey    = await importPublicKey(recipientPublicKeyJwk);
  const sharedKey          = await deriveSharedKey(ephemeral.privateKey, recipientPubKey);

  const iv              = crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: AES_ALGO, iv }, sharedKey, bytes
  );

  return {
    ciphertext:         bufferToBase64(ciphertextBuffer),
    iv:                 bufferToBase64(iv),
    ephemeralPublicKey, // plain JWK object — NOT stringified
  };
};

/* ─────────────────────────────────────────────────────────────
   DM ENCRYPTION — text string → envelope
───────────────────────────────────────────────────────────── */
export const encryptMessage = async (plaintext, recipientPublicKeyJwk) => {
  if (!plaintext || !recipientPublicKeyJwk) return null;
  try {
    return await _encryptBytes(new TextEncoder().encode(plaintext), recipientPublicKeyJwk);
  } catch (e) {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   DM ENCRYPTION — any JS object → envelope
───────────────────────────────────────────────────────────── */
export const encryptObject = async (obj, recipientPublicKeyJwk) => {
  if (!obj || !recipientPublicKeyJwk) return null;
  try {
    const encoded = new TextEncoder().encode(JSON.stringify(obj));
    return await _encryptBytes(encoded, recipientPublicKeyJwk);
  } catch (e) {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   DM DECRYPTION — envelope → plaintext string
───────────────────────────────────────────────────────────── */
export const decryptMessage = async (
  ciphertext, iv, ephemeralPublicKeyRaw, myPrivateKey
) => {
  if (!ciphertext || !iv || !ephemeralPublicKeyRaw || !myPrivateKey) {
    return null;
  }

  try {
    let jwk = ephemeralPublicKeyRaw;
    if (typeof jwk === "string") {
      try { jwk = JSON.parse(jwk); }
      catch { return null; }
    }

    if (!jwk || jwk.kty !== "EC" || jwk.crv !== "P-256" || !jwk.x || !jwk.y) {
      return null;
    }

    const ephemeralPubKey = await importPublicKey(jwk);
    const sharedKey       = await deriveSharedKey(myPrivateKey, ephemeralPubKey);

    const decrypted = await crypto.subtle.decrypt(
      { name: AES_ALGO, iv: base64ToBuffer(iv) },
      sharedKey,
      base64ToBuffer(ciphertext)
    );

    return new TextDecoder().decode(decrypted);
  } catch (e) {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   DM DECRYPTION — envelope → parsed JS object
───────────────────────────────────────────────────────────── */
export const decryptObject = async (
  ciphertext, iv, ephemeralPublicKeyRaw, myPrivateKey
) => {
  const json = await decryptMessage(ciphertext, iv, ephemeralPublicKeyRaw, myPrivateKey);
  if (!json) return null;
  try { return JSON.parse(json); }
  catch (e) { return null; }
};

/* ─────────────────────────────────────────────────────────────
   🔐 PRIVATE KEY BACKUP (PASSWORD-ENCRYPTED)
───────────────────────────────────────────────────────────── */

/**
 * Derive AES key from password using PBKDF2
 */
const deriveBackupKey = async (password, salt) => {
  const enc = new TextEncoder();

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: base64ToBuffer(salt),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

/**
 * Encrypt private key JWK with password
 */
export const encryptPrivateKeyBackup = async (privateJwk, password) => {
  const enc = new TextEncoder();

  const salt = bufferToBase64(crypto.getRandomValues(new Uint8Array(16)));
  const iv   = crypto.getRandomValues(new Uint8Array(12));

  const key = await deriveBackupKey(password, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(JSON.stringify(privateJwk))
  );

  return {
    encryptedPrivateKey: bufferToBase64(ciphertext),
    iv: bufferToBase64(iv),
    salt,
  };
};

/**
 * Decrypt private key JWK using password
 */
export const decryptPrivateKeyBackup = async (
  encryptedPrivateKey,
  password,
  salt
) => {
  try {
    const dec = new TextDecoder();

    // 🔥 handle stored JSON format
    let parsed;
    try {
      parsed = JSON.parse(encryptedPrivateKey);
    } catch {
      return null;
    }

    const { ciphertext, iv } = parsed;

    const key = await deriveBackupKey(password, salt);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: base64ToBuffer(iv) },
      key,
      base64ToBuffer(ciphertext)
    );

    return JSON.parse(dec.decode(decrypted));

  } catch (err) {
    return null;
  }
};

export async function exportPrivateKey(
    privateKey
) {
    return crypto.subtle.exportKey(
        "jwk",
        privateKey
    );
}

/* ─────────────────────────────────────────────────────────────
   🔐 USWD SIGNING KEYS (Ed25519)
───────────────────────────────────────────────────────────── */

/**
 * Generate Ed25519 signing keypair
 */
export const generateSigningKeyPair =
async () => {

  const keyPair =
    await crypto.subtle.generateKey(
      {
        name: "Ed25519",
      },
      true,
      ["sign", "verify"]
    );

  const publicJwk =
    await crypto.subtle.exportKey(
      "jwk",
      keyPair.publicKey
    );

  const privateJwk =
    await crypto.subtle.exportKey(
      "jwk",
      keyPair.privateKey
    );

  return {
    privateKey:
      keyPair.privateKey,

    privateJwk,

    publicJwk,
  };

};


/**
 * Save signing keypair
 * Uses chatId (not Mongo userId)
 */
export const saveSigningKeyPair =
async (
  chatId,
  privateKey,
  publicJwk
) => {

  await saveSigningPrivateKey(
    chatId,
    privateKey
  );

  localStorage.setItem(
    `${SIGNING_PUBKEY_STORAGE_PREFIX}${chatId}`,
    JSON.stringify(
      publicJwk
    )
  );

};


/**
 * Load signing private key
 */
export const loadSigningPrivateKey =
async (
  chatId
) => {

  try {

    return (
      await loadSigningPrivateKeyFromDB(
        chatId
      )
    ) || null;

  } catch {

    return null;

  }

};


/**
 * Load signing public JWK
 */
export const loadSigningPublicKeyJwk =
(
  chatId
) => {

  try {

    const raw =
      localStorage.getItem(
        `${SIGNING_PUBKEY_STORAGE_PREFIX}${chatId}`
      );

    if (!raw) {
      return null;
    }

    return JSON.parse(raw);

  } catch {

    return null;

  }

};


/**
 * Import Ed25519 private key
 */
export const importSigningPrivateKey =
async (
  jwk
) =>

  crypto.subtle.importKey(
    "jwk",
    jwk,
    {
      name: "Ed25519",
    },
    true,
    ["sign"]
  );


/**
 * Import Ed25519 public key
 */
export const importSigningPublicKey =
async (
  jwk
) =>

  crypto.subtle.importKey(
    "jwk",
    jwk,
    {
      name: "Ed25519",
    },
    true,
    ["verify"]
  );


/**
 * Sign payload
 */
export const signPayload =
async (
  payload,
  privateKey
) => {

  try {

    const encoded =
      new TextEncoder()
      .encode(
        JSON.stringify(
          payload
        )
      );

    const signature =
      await crypto.subtle.sign(
        "Ed25519",
        privateKey,
        encoded
      );

    return bufferToBase64(
      signature
    );

  } catch (err) {

    return null;

  }

};

export const signString =
async (
  value,
  privateKey
) => {

  try {

    const encoded =
      new TextEncoder()
      .encode(value);

    const signature =
      await crypto.subtle.sign(
        "Ed25519",
        privateKey,
        encoded
      );

    return bufferToBase64(
      signature
    );

  } catch (err) {

    return null;

  }

};



/**
 * Verify signature
 */
export const verifyPayloadSignature =
async (
  payload,
  signatureBase64,
  publicKey
) => {

  try {

    const encoded =
      new TextEncoder()
      .encode(
        JSON.stringify(
          payload
        )
      );

    return await crypto.subtle.verify(
      "Ed25519",
      publicKey,
      base64ToBuffer(
        signatureBase64
      ),
      encoded
    );

  } catch (err) {

    return false;

  }

};

/* ─────────────────────────────────────────────────────────────
   🔐 USWD PRIVATE KEY BACKUP
───────────────────────────────────────────────────────────── */

/**
 * Derive backup key specifically for USWD
 */
const deriveUswdBackupKey =
async (
  password,
  salt
) => {

  const enc =
    new TextEncoder();

  const keyMaterial =
    await crypto.subtle.importKey(
      "raw",
      enc.encode(password),
      "PBKDF2",
      false,
      ["deriveKey"]
    );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",

      salt:
        base64ToBuffer(salt),

      iterations:
        250000,

      hash:
        "SHA-512",

    },

    keyMaterial,

    {
      name: "AES-GCM",
      length: 256,
    },

    false,

    [
      "encrypt",
      "decrypt",
    ]
  );

};


/**
 * Encrypt USWD signing private key
 */
export const encryptUswdPrivateKeyBackup =
async (
  privateJwk,
  password
) => {

  const enc =
    new TextEncoder();

  const salt =
    bufferToBase64(
      crypto.getRandomValues(
        new Uint8Array(32)
      )
    );

  const iv =
    crypto.getRandomValues(
      new Uint8Array(16)
    );

  const key =
    await deriveUswdBackupKey(
      password,
      salt
    );

  const ciphertext =
    await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
      },
      key,
      enc.encode(
        JSON.stringify(
          privateJwk
        )
      )
    );

  return {

    encryptedPrivateKey:
      JSON.stringify({

        ciphertext:
          bufferToBase64(
            ciphertext
          ),

        iv:
          bufferToBase64(
            iv
          ),

      }),

    salt,

  };

};


/**
 * Decrypt USWD signing private key
 */
export const decryptUswdPrivateKeyBackup =
async (
  encryptedPrivateKey,
  password,
  salt
) => {

  try {

    const dec =
      new TextDecoder();

    const {
      ciphertext,
      iv,
    } =
      JSON.parse(
        encryptedPrivateKey
      );

    const key =
      await deriveUswdBackupKey(
        password,
        salt
      );

    const decrypted =
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv:
            base64ToBuffer(iv),
        },
        key,
        base64ToBuffer(
          ciphertext
        )
      );

    return JSON.parse(
      dec.decode(
        decrypted
      )
    );

  } catch (err) {

    return null;

  }

};

export async function exportSigningPrivateKey(
    privateKey
) {
    return crypto.subtle.exportKey(
        "jwk",
        privateKey
    );
}

export const generateSolanaWallet =
() => {

  return Keypair.generate();

};

export const saveSolanaWallet =
async (
  chatId,
  wallet
) => {

  if (
    !chatId ||
    !wallet
  ) {
    return;
  }

  await saveSolanaPrivateKey(

  chatId,

  Array.from(
    wallet.secretKey
  )

);
};

export const loadSolanaWallet =
async (
  chatId
) => {

  try {

    const secretKey =
      await loadSolanaPrivateKey(
        chatId
      );

    if (!secretKey) {
      return null;
    }

    return Keypair.fromSecretKey(
      Uint8Array.from(secretKey)
    );

  } catch {

    return null;

  }

};


export const encryptSolanaWalletBackup =
async (
  wallet,
  password
) => {

  const secretKey =
    Array.from(
      wallet.secretKey
    );

  return encryptUswdPrivateKeyBackup(

    secretKey,

    password

  );

};

export const decryptSolanaWalletBackup =
async (
  backup,
  password
) => {

  if (!backup) {

    return null;

  }

  const secretKey =
    await decryptUswdPrivateKeyBackup(

      backup.encryptedPrivateKey,

      password,

      backup.salt

    );

  if (!secretKey) {

    return null;

  }

  return Keypair.fromSecretKey(

    Uint8Array.from(
      secretKey
    )

  );

};

/**
 * Sign arbitrary payload using a Solana wallet.
 *
 * Returns a Base64 signature.
 */
export const signSolanaPayload =
async (
  payload,
  wallet
) => {

  try {

    if (
      !wallet?.secretKey
    ) {

      return null;

    }

    const encoded =
      new TextEncoder()
      .encode(
        JSON.stringify(
          payload
        )
      );

    const signature =
      nacl.sign.detached(

        encoded,

        wallet.secretKey

      );

    return bufferToBase64(
      signature
    );

  } catch (err) {

    return null;

  }

};


/**
 * Verify a Solana payload signature.
 *
 * Returns true/false.
 */
export const verifySolanaPayloadSignature =
async (
  payload,
  signatureBase64,
  publicKey
) => {

  try {

    if (
      !publicKey
    ) {

      return false;

    }

    const encoded =
      new TextEncoder()
      .encode(
        JSON.stringify(
          payload
        )
      );

    return nacl.sign.detached.verify(

      encoded,

      new Uint8Array(
        base64ToBuffer(
          signatureBase64
        )
      ),

      publicKey.toBytes()

    );

  } catch (err) {

    return false;

  }

};

/* ─────────────────────────────────────────────────────────────
   GROUP KEY MANAGEMENT — Shared Symmetric Key
─────────────────────────────────────────────────────────────

   generateGroupKey()
     Generates a fresh AES-GCM-256 CryptoKey for a group.
     Called once at group creation or key rotation.

   exportGroupKey(key) → base64
     Exports the raw 32 bytes of the AES key as a base64 string.
     This is what gets ECDH-wrapped for each member.

   importGroupKey(base64) → CryptoKey
     Re-imports a base64 raw key back into a usable CryptoKey.

   saveGroupKey(groupId, base64)
     Persists the raw base64 key to localStorage.

   loadGroupKey(groupId) → CryptoKey | null
     Loads and imports the group key from localStorage.

   encryptGroupKeyForMember(rawKeyBase64, memberPublicKeyJwk)
     ECDH-wraps the raw group key bytes for a specific member.
     Returns a DM-style envelope { ciphertext, iv, ephemeralPublicKey }.

   decryptGroupKey(envelope, myPrivateKey) → base64 | null
     Unwraps a member's copy of the group key using their private key.
     Returns the raw base64 key bytes.
───────────────────────────────────────────────────────────── */

export const generateGroupKey = async () =>
  crypto.subtle.generateKey(
    { name: AES_ALGO, length: AES_KEY_LEN },
    false, // 🔥 NON-EXTRACTABLE
    ["encrypt", "decrypt"]
  );

export const exportGroupKey = async (cryptoKey) => {
  const raw = await crypto.subtle.exportKey("raw", cryptoKey);
  return bufferToBase64(raw);
};

export const importGroupKey = async (base64) => {
  const raw = base64ToBuffer(base64);
  return crypto.subtle.importKey(
    "raw", raw,
    { name: AES_ALGO, length: AES_KEY_LEN },
    false,       // not re-extractable once imported for use
    ["encrypt", "decrypt"]
  );
};

// ✅ Save CryptoKey directly (non-extractable)
export const saveGroupKey = async (groupId, cryptoKey) => {
  if (!groupId || !cryptoKey) return;
  await saveGroupCryptoKey(groupId, cryptoKey);
};

// ✅ Load CryptoKey directly
export const loadGroupKey = async (groupId) => {
  try {
    return await loadGroupCryptoKey(groupId);
  } catch (e) {
    return null;
  }
};

/**
 * Wrap the raw group key bytes for a specific member using ECDH.
 * Returns a DM-style envelope the member can decrypt with their private key.
 */
export const encryptGroupKeyForMember = async (rawKeyBase64, memberPublicKeyJwk) => {
  if (!rawKeyBase64 || !memberPublicKeyJwk) return null;
  try {
    const bytes = base64ToBuffer(rawKeyBase64);
    return await _encryptBytes(bytes, memberPublicKeyJwk);
  } catch (e) {
    return null;
  }
};

/**
 * Unwrap a member's copy of the group key.
 * Returns the raw base64 key string, or null on failure.
 */
export const decryptGroupKey = async (envelope, myPrivateKey) => {
  if (!envelope?.ciphertext || !envelope?.iv || !envelope?.ephemeralPublicKey || !myPrivateKey) {
    return null;
  }
  try {
    let jwk = envelope.ephemeralPublicKey;
    if (typeof jwk === "string") {
      try { jwk = JSON.parse(jwk); } catch { return null; }
    }
    if (!jwk || jwk.kty !== "EC" || jwk.crv !== "P-256" || !jwk.x || !jwk.y) return null;

    const ephemeralPubKey = await importPublicKey(jwk);
    const sharedKey       = await deriveSharedKey(myPrivateKey, ephemeralPubKey);

    const decrypted = await crypto.subtle.decrypt(
      { name: AES_ALGO, iv: base64ToBuffer(envelope.iv) },
      sharedKey,
      base64ToBuffer(envelope.ciphertext)
    );

    return bufferToBase64(decrypted); // raw AES key bytes as base64
  } catch (e) {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   GROUP ENCRYPTION — encrypt with shared AES key
─────────────────────────────────────────────────────────────

   encryptWithGroupKey(plaintext, groupCryptoKey)
     Encrypts a string with the shared group AES-GCM key.
     Returns { ciphertext, iv } — NO ephemeralPublicKey needed
     since this is symmetric (everyone has the same key).

   encryptObjectWithGroupKey(obj, groupCryptoKey)
     Same but for any serialisable object (e.g. media metadata).

   decryptWithGroupKey(ciphertext, iv, groupCryptoKey)
     Decrypts with the shared group key → string.

   decryptObjectWithGroupKey(ciphertext, iv, groupCryptoKey)
     Decrypts → parsed JS object.
───────────────────────────────────────────────────────────── */

export const encryptWithGroupKey = async (plaintext, groupCryptoKey) => {
  if (!plaintext || !groupCryptoKey) return null;
  try {
    const iv              = crypto.getRandomValues(new Uint8Array(12));
    const encoded         = new TextEncoder().encode(plaintext);
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: AES_ALGO, iv }, groupCryptoKey, encoded
    );
    return {
      ciphertext: bufferToBase64(ciphertextBuffer),
      iv:         bufferToBase64(iv),
    };
  } catch (e) {
    return null;
  }
};

export const encryptObjectWithGroupKey = async (obj, groupCryptoKey) => {
  if (!obj || !groupCryptoKey) return null;
  try {
    const json            = JSON.stringify(obj);
    const iv              = crypto.getRandomValues(new Uint8Array(12));
    const encoded         = new TextEncoder().encode(json);
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: AES_ALGO, iv }, groupCryptoKey, encoded
    );
    return {
      ciphertext: bufferToBase64(ciphertextBuffer),
      iv:         bufferToBase64(iv),
    };
  } catch (e) {
    return null;
  }
};

export const decryptWithGroupKey = async (ciphertext, iv, groupCryptoKey) => {
  if (!ciphertext || !iv || !groupCryptoKey) return null;
  try {
    const decrypted = await crypto.subtle.decrypt(
      { name: AES_ALGO, iv: base64ToBuffer(iv) },
      groupCryptoKey,
      base64ToBuffer(ciphertext)
    );
    return new TextDecoder().decode(decrypted);
  } catch (e) {
    return null;
  }
};

export const decryptObjectWithGroupKey = async (ciphertext, iv, groupCryptoKey) => {
  const json = await decryptWithGroupKey(ciphertext, iv, groupCryptoKey);
  if (!json) return null;
  try { return JSON.parse(json); }
  catch (e) { return null; }
};

/* ─────────────────────────────────────────────────────────────
   STATUS KEY MANAGEMENT — Shared Symmetric Key (mirrors GROUP)

   Status keys MUST be extractable because the creator exports
   the raw AES key and uploads it to the server, where it is
   wrapped individually for followers.
───────────────────────────────────────────────────────────── */

export const generateStatusKey = async () =>
  crypto.subtle.generateKey(
    {
      name: AES_ALGO,
      length: AES_KEY_LEN,
    },
    true, // ✅ extractable
    ["encrypt", "decrypt"]
  );

  export const exportStatusKey = async (
  cryptoKey
) => {

  const raw =
    await crypto.subtle.exportKey(
      "raw",
      cryptoKey
    );

  return bufferToBase64(raw);

};

export const importStatusKey = async (
  base64
) => {

  const raw =
    base64ToBuffer(base64);

  return crypto.subtle.importKey(

    "raw",

    raw,

    {
      name: AES_ALGO,
      length: AES_KEY_LEN,
    },

    true, // <-- extractable

    [
      "encrypt",
      "decrypt",
    ]

  );

};
export const encryptStatusKeyForFollower =
  encryptGroupKeyForMember;

export const decryptStatusKey =
  decryptGroupKey;

export const encryptWithStatusKey =
  encryptWithGroupKey;

export const decryptWithStatusKey =
  decryptWithGroupKey;

export const encryptObjectWithStatusKey =
  encryptObjectWithGroupKey;

export const decryptObjectWithStatusKey =
  decryptObjectWithGroupKey;

  export const saveStatusKey = async (
  ownerChatId,
  cryptoKey
) => {
  if (!ownerChatId || !cryptoKey) return;
  await saveStatusCryptoKey(
    ownerChatId,
    cryptoKey
  );
};

export const loadStatusKey = async (
  ownerChatId
) => {
  try {
    return await loadStatusCryptoKey(
      ownerChatId
    );
  } catch {
    return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   E2EE INIT
───────────────────────────────────────────────────────────── */
export const initE2EE = async (chatId) => {
  const privateKey = await loadPrivateKey(chatId);
  const publicKey  = loadPublicKeyJwk(chatId);

  return {
    privateKey,
    publicKey,
  };
};
/* ─────────────────────────────────────────────────────────────
   PUBLIC KEY CACHE
───────────────────────────────────────────────────────────── */
const pubKeyCache = new Map();

// 🔥 GENERIC (works for BOTH self + peers)
export const getPublicKey        = (chatId) => pubKeyCache.get(String(chatId)) || null;
export const cachePublicKey      = (chatId, jwk) => pubKeyCache.set(String(chatId), jwk);
export const clearPublicKeyCache = () => pubKeyCache.clear();

/* ─────────────────────────────────────────────────────────────
   🔐 USWD SIGNING PUBLIC KEY CACHE
───────────────────────────────────────────────────────────── */

const uswdSigningPubKeyCache =
  new Map();

export const getUswdSigningPublicKey =
  (chatId) =>
    uswdSigningPubKeyCache.get(
      String(chatId)
    ) || null;

export const cacheUswdSigningPublicKey =
  (chatId, jwk) =>
    uswdSigningPubKeyCache.set(
      String(chatId),
      jwk
    );

export const clearUswdSigningPublicKeyCache =
  () =>
    uswdSigningPubKeyCache.clear();


  const statusKeyCache = new Map();

export const getCachedStatusKey = (ownerChatId) =>
  statusKeyCache.get(String(ownerChatId)) || null;

export const cacheStatusKey = (ownerChatId, cryptoKey) =>
  statusKeyCache.set(String(ownerChatId), cryptoKey);

export const clearStatusKeyCache = (ownerChatId) => {

  if (ownerChatId) {

    statusKeyCache.delete(String(ownerChatId));

  } else {

    statusKeyCache.clear();

  }

};
/* ─────────────────────────────────────────────────────────────
   BASE64 HELPERS
───────────────────────────────────────────────────────────── */
const bufferToBase64 = (buffer) => {
  const bytes     = new Uint8Array(buffer);
  let binary      = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};